import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgOtpInputComponent } from 'ng-otp-input';
import { ActivatedRoute, Router } from '@angular/router';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { CommonService } from 'src/app/services/common/common.service';
import { MonitorService } from 'src/app/services/monitor/monitor.service';
import { CoreService } from 'src/app/services/core/core.service';
import { firstValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from 'src/app/config/configuration/service/configuration.service';
dayjs.extend(utc);
@Component({
  selector: 'verify-otp',
  templateUrl: './verify-otp.component.html',
  styleUrls: [ './verify-otp.component.scss' ],
  standalone: false
})
export class VerifyOtpComponent implements OnInit {
  submitted: boolean = false;
  form: any;
  formInput = [ 'input1', 'input2', 'input3', 'input4' ];
  selected_country: any;
  correct_otp: number = 0;
  resend: boolean = false;
  isScroll = false;
  seconds = 30;
  resendCounter = 2;
  currentSize = 0;
  currentHeight: any;
  currentOTPValue: string = '';
  selectedEmail: string = '';
  selectedPhoneNo: string = '';
  action: string = '';
  currentModule = 'claims';
  source = 'email';
  isOtpScreen: boolean = false;

  @ViewChildren('formRow') rows: any;
  @ViewChild(NgOtpInputComponent, { static: false }) ngOtpInput: NgOtpInputComponent;

  otpConfig: any = {
    length: 4,
    allowNumbersOnly: true,
    disableAutoFocus: true,
    inputClass: ''
  };

  @ViewChild('ngOtpInput') ngOtpInputRef: any;
  abortController: any;
  private token = '';
  timer: any;
  otpLoading: boolean = false;
  isLinkExpired: boolean = false;
  externalToken: string = '';
  authentication: string = '';
  isLanguageSelected: boolean = false;
  selectedLang: string = 'en';
  languages: Array<{ Code: string; Name: string; Direction: string; Locale: string; Prompt: string }> = [
    { Code: 'en', Name: 'English', Direction: 'ltr', Locale: 'en', Prompt: 'Please choose your language to proceed.' },
    { Code: 'ar', Name: 'العربية', Direction: 'rtl', Locale: 'ar-AE', Prompt: 'يرجى اختيار لغتك للمتابعة.' }
  ];

  /**
   *
   * @param httpClient
   * @param commonService
   * @param router
   * @param networkService
   * @param monitorService
   * @param eRef
   */
  constructor(
    private coreService: CoreService,
    private commonService: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private monitorService: MonitorService,
    private eRef: ElementRef,
    private translate: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly configurationService: ConfigurationService
  ) {
    this.selectedLang = this.configurationService.languageSettings?.selectedLanguage || 'en';
    this.form = this.toFormGroup(this.formInput);
    this.token = this.route.snapshot.queryParams['token'];
    const obj: any = atob(this.token);
    const parsedObj = JSON.parse(obj);
    this.selectedEmail = parsedObj.GarageEmail || parsedObj.Email;
    this.selectedPhoneNo = parsedObj.MobileNumber;
    this.action = parsedObj.Action;
    this.source = this.route.snapshot.queryParams['source'] || 'email';
    this.externalToken = parsedObj.ExternalToken;
    this.authentication = parsedObj.Authentication;
    this.route.data?.subscribe((data: any) => {
      this.currentModule = data.module || 'claims';
      this.cdr.markForCheck();
    });
    const expiryTime = dayjs.utc(parsedObj.LinkExpirationTimeStamp);
    if (dayjs().isAfter(expiryTime)) {
      this.isLinkExpired = true;
    }
  }

  /**
   * init
   */
  ngOnInit(): void {
    this.monitorService.logEvent('verifyOtp', [
      'VerifyOtp',
      'addenda-claim',
      {
        email: this.selectedEmail
      }
    ]);
    this.commonService.dismissBootstrapLoader();
  }


  /**
   * Applies the language picked on the landing card and starts the OTP / secure-link flow.
   * @param code language code (`en` | `ar`)
   * @returns resolves once the translations are applied
   */
  async selectLanguage(code: string): Promise<void> {
    await this.applyLanguage(code);
    if (!this.isLanguageSelected) {
      this.isLanguageSelected = true;
      this.startVerification();
    }
    this.cdr.markForCheck();
  }

  /**
   * Switches language from the top-corner switch without restarting the OTP flow.
   * @param code language code (`en` | `ar`)
   * @returns resolves once the translations are applied
   */
  async onLangChange(code: string): Promise<void> {
    if (code === this.selectedLang) {
      return;
    }
    await this.applyLanguage(code);
    this.cdr.markForCheck();
  }

  /**
   * Persists the language and applies translations, locale and document direction in place (no reload).
   * @param code language code (`en` | `ar`)
   * @returns resolves once the translations are applied
   */
  private async applyLanguage(code: string): Promise<void> {
    const lang = this.languages.find(x => x.Code === code);
    if (!lang) {
      return;
    }
    this.selectedLang = lang.Code;
    this.configurationService.languageSettings.selectedLanguage = lang.Code;
    localStorage.setItem('languageSettings', JSON.stringify(this.configurationService.languageSettings));
    localStorage.setItem('languageCode', lang.Code);
    localStorage.setItem('locale', lang.Locale);
    await this.configurationService.setTranslation();
    this.commonService.loadLocaleMessages(lang.Code);
    this.commonService.setLocale(lang.Locale);
  }

  /**
   * Starts the resend timer and either verifies the secure link or sends the first OTP.
   */
  private startVerification(): void {
    if (this.isLinkExpired) {
      return;
    }
    setTimeout(() => {
      this.resend = true;
      this.cdr.markForCheck();
    }, 30000);

    if (this.authentication == 'securelink') {
      this.verifySecuredLink();
    } else {
      this.isOtpScreen = true;
      this.sendOtp(false);
      this.updateSeconds();
    }
  }

  /**
   *
   * @param event
   */
  @HostListener('document:click', [ '$event' ])
  clickout(event: Event): void {
    if (!this.eRef.nativeElement.contains(event.target)) {
      const container: any = document.querySelector('.verify-otp-wrapper');
      if (this.currentHeight) {
        document.documentElement.style.setProperty('--vh', `${this.currentHeight}px`);
      }
      if (container) {
        container.scrollTop = 0;
      }
    }
  }

  /**
   * opt request listiner
   */
  otpRequestListener(): void {
    if ('OTPCredential' in window) {
      this.abortController = new AbortController();
      this.timer = setTimeout(() => {
        this.abortController.abort();
        this.otpRequestListener();
      }, 30 * 1000);

      const o: any = {
        otp: { transport: [ 'sms' ] },
        signal: this.abortController.signal
      };
      window.navigator['credentials']
        .get(o)
        .then((sms: any) => {
          const content = sms.code;
          this.monitorService.logEvent('sms otp from credentials', [
            'VerifyOtp',
            'addenda-claim',
            {
              otp: content
            }
          ]);
          this.ngOtpInput.setValue(content);
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }

  /**
   * resize event listiner
   */
  @HostListener('window:resize', [ '$event' ])
  onResize(event: any): void {
    const size = window.innerWidth + window.innerHeight;
    this.monitorService.logEvent('resize ', [
      'Verify OTP',
      'addenda-claim',
      {
        originalSize: this.currentSize,
        resize: size
      }
    ]);
    if (size != this.currentSize) {
      this.isScroll = true;
    } else {
      this.isScroll = false;
    }
  }

  /**
   * toFormGroup
   */
  toFormGroup(elements: any): FormGroup {
    const group: any = {};
    elements.forEach((key: any) => {
      group[key] = new FormControl('', Validators.required);
    });
    return new FormGroup(group);
  }

  errorMessages = '';

  /**
   * verify otp
   * @param otp
   */
  verifyOtp(): void {
    if (this.otpLoading) {return;}
    this.monitorService.logEvent('verifyOtp ', [
      'VerifyOTP',
      'addenda-claim',
      {
        otp: this.currentOTPValue
      }
    ]);
    let source = 'email';
    if (this.source !== 'email') {source = 'phone';}
    let param = this.selectedEmail;
    if (this.source !== 'email') {param = this.selectedPhoneNo;}

    const payload = {
      otpCode: this.currentOTPValue,
      userIdType: source,
      isExternalToken: 'true',
      emailorPhoneNumber: param
    };
    this.otpLoading = true;
    this.commonService.showLoading();

    this.coreService.generateTokenFromOTP(payload).subscribe({
      next: async (res: any) => {
        if (res.success) {
          const obj = {
            accessToken: res?.data.accessToken,
            refreshToken: res?.data.refreshToken
          };
          this.coreService.generateRefreshToken(obj).subscribe({
            next: (res: any) => {
              this.commonService.hideLoading();
              const token = { idToken: res?.data.accessToken };
              localStorage.setItem('XAuserDetails', JSON.stringify(token));
              if (res && this.action) {
                if (this.currentModule !== 'claims') {
                  localStorage.setItem('XAuserDetailsExternal', JSON.stringify(token)); // Going forward will get token in auth interceptor for external user
                  this.router.navigateByUrl(`${this.currentModule}/${this.action}?token=${this.token}`);
                } else {
                  // if (this.currentModule !== 'claims') {
                  //   this.router.navigateByUrl(`${this.currentModule}/${this.action}?token=${this.token}`);
                  // } else {

                  this.router.navigateByUrl(`claim/${this.action}?token=${this.token}`);
                }
              } else {
                this.errorMessages = res.errors;
                this.correct_otp = 3;
                this.otpConfig.inputClass = 'border-red';
              }
              this.cdr.markForCheck();
            },
            error: (error) => {
              try {
                if (
                  error &&
                  error.error &&
                  // eslint-disable-next-line max-len
                  error.error.msg.indexOf(this.translate.instant('otp_entered_many_times')) != -1
                ) {
                  this.errorMessages = error.error.msg;
                } else {
                  this.errorMessages = error.error.msg;
                  if (!this.errorMessages) {
                    this.errorMessages = this.translate.instant('something_went_wrong');
                  }
                }
              } catch (e) {
                this.errorMessages = this.translate.instant('something_went_wrong');
              } finally {
                this.correct_otp = 3;
                this.otpConfig.inputClass = 'border-red';
              }
              this.monitorService.logException(error, 1);
              this.cdr.markForCheck();
            }
          });
        } else {
          this.errorMessages = res.errors;
          this.correct_otp = 3;
          this.otpConfig.inputClass = 'border-red';
        }
        this.otpLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.otpLoading = false;
        try {
          if (error && error.error && error.error.msg.indexOf(this.translate.instant('otp_entered_many_times')) != -1) {
            this.errorMessages = error.error.msg;
          } else {
            this.errorMessages = error.error.msg;
            if (!this.errorMessages) {
              this.errorMessages = this.translate.instant('something_went_wrong');
            }
          }
        } catch (e: any) {
          this.errorMessages = this.translate.instant('something_went_wrong');
          this.monitorService.logException(e, SeverityLevel.Error);
        } finally {
          this.correct_otp = 3;
          this.otpConfig.inputClass = 'border-red';
        }
        this.monitorService.logException(error, 1);
      }
    });
  }
  /**
   * update seconds
   */
  updateSeconds(): void {
    setTimeout(() => {
      if (this.seconds > 0) {
        this.seconds--;
        this.cdr.markForCheck();
        this.updateSeconds();
      }
    }, 1000);
  }

  /**
   *
   */
  verifySecuredLink(): void {
    this.commonService.showLoading();

    this.coreService.getSecuredLink(this.externalToken).subscribe({
      next: (res: any) => {
        if (res.success) {
          const obj = {
            accessToken: res?.data.accessToken,
            refreshToken: res?.data.refreshToken
          };
          this.coreService.generateRefreshToken(obj).subscribe({
            next: (res: any) => {
              this.commonService.hideLoading();
              const token = { idToken: res?.data.accessToken };
              localStorage.setItem('XAuserDetails', JSON.stringify(token));
              if (res && this.action) {
                localStorage.setItem('XAuserDetailsExternal', JSON.stringify(token));
                this.router.navigateByUrl(`${this.currentModule}/${this.action}?token=${this.token}`);
              }
              this.cdr.markForCheck();
            },
            error: (error) => {
              this.errorMessages = this.translate.instant('something_went_wrong');
              this.monitorService.logException(error, 1);
              this.cdr.markForCheck();
            }
          });
        }
        this.commonService.hideLoading();
      },
      error: () => {
        this.commonService.hideLoading();
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * send otp
   */
  sendOtp(resetCounter: boolean = true): void {
    this.commonService.showLoading();
    let functionToCall = 'generateEmailOTP';
    if (this.source !== 'email') {functionToCall = 'generateSmsOrWhatsappOTP';}
    let param = this.selectedEmail;
    if (this.source !== 'email') {param = this.selectedPhoneNo;}
    firstValueFrom(this.coreService[functionToCall](param))
      .then((res: any) => {
        if (resetCounter) {
          this.resend = false;
          this.resendCounter = this.resendCounter - 1;
          setTimeout(() => {
            this.resend = true;
            this.cdr.markForCheck();
          }, 30000);
          this.seconds = 30;
          this.updateSeconds();
        }

        this.errorMessages = '';
        this.correct_otp = 0;
        this.otpConfig.inputClass = '';
        this.ngOtpInput.setValue('');
        const inputs = document.getElementsByClassName('otp-input');
        if (inputs) {
          for (let i = 0; i < inputs.length; i++) {
            (inputs[i] as HTMLInputElement).disabled = false;
          }
        }
        this.commonService.hideLoading();
        this.cdr.markForCheck();
      })
      .catch((err) => {
        this.commonService.hideLoading();
        this.monitorService.logException(err, SeverityLevel.Error);
        this.cdr.markForCheck();
      });
  }

  /**
   * resend sms
   */
  resend_otp(): void {
    if (this.resendCounter === 0) {
      return;
    }
    const header = '';
    this.monitorService.logEvent('resend_otp ', [ 'VerifyOTP', 'addenda-claim' ]);
    let message = this.translate.instant('resend_otp_email', { email: this.selectedEmail });
    if (this.source !== 'email') {message = this.translate.instant('resend_otp_phone', { phone: this.selectedPhoneNo });}

    const dialogRef = this.commonService.openConfirmYesNoDialogBig(header, message, '', 'ok', 'cancel');
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.sendOtp();
      }
    });
  }

  /**
   * onOtpChange event
   */
  onOtpChange(event: any): void {
    this.monitorService.logEvent('onOtpChange ', [ 'VerifyOTP', 'addenda-claim' ]);
    if (event.length == 4) {
      this.currentOTPValue = event;
      this.correct_otp = 1;
    } else {
      this.correct_otp = 0;
    }
  }
}
